/**
 * GSM 03.38 (GSM-7) character set and single-segment checks.
 *
 * One SMS segment holds 160 septets of GSM-7. Characters in the basic set cost one septet;
 * characters in the extension table (^ { } \ [ ~ ] | and the euro sign) cost two, because
 * they are sent as ESC + char. A single character outside both tables (an emoji, a curly
 * quote, an em dash) switches the whole message to UCS-2, which holds only 70 characters
 * per segment, so every message this app sends must stay inside GSM-7 and within 160.
 */

/** The basic table, minus ESC (0x1B), which is never sent on its own. */
const BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'

/** The extension table, minus form feed and the single shift codes. */
const EXTENDED = '^{}\\[~]|€'

const BASIC_SET = new Set(BASIC)
const EXTENDED_SET = new Set(EXTENDED)

export const SEGMENT_SEPTETS = 160

/** Septets one character costs, or null when it is not GSM-7. */
export function septetsOf(char: string): 1 | 2 | null {
  if (BASIC_SET.has(char)) return 1
  if (EXTENDED_SET.has(char)) return 2
  return null
}

export type SegmentCheck = {
  ok: boolean
  /** Septets used. Characters outside GSM-7 are not counted. */
  septets: number
  /** Septets left in one segment (negative when over). */
  remaining: number
  /** Distinct characters that are not GSM-7, in order of appearance. */
  invalid: string[]
}

/** Checks that `text` is GSM-7 and fits in one 160-septet segment. */
export function checkSegment(text: string): SegmentCheck {
  let septets = 0
  const invalid: string[] = []
  // Iterate by code point so an emoji counts as one bad character, not two halves.
  for (const char of text) {
    const cost = septetsOf(char)
    if (cost === null) {
      if (!invalid.includes(char)) invalid.push(char)
    } else {
      septets += cost
    }
  }
  return {
    ok: invalid.length === 0 && septets <= SEGMENT_SEPTETS,
    septets,
    remaining: SEGMENT_SEPTETS - septets,
    invalid,
  }
}

export class SegmentError extends Error {
  override name = 'SegmentError'
  constructor(
    message: string,
    readonly check: SegmentCheck,
  ) {
    super(message)
  }
}

/** Throws a SegmentError unless `text` is one GSM-7 segment. The last gate before sending. */
export function assertSingleSegment(text: string): void {
  const check = checkSegment(text)
  if (check.invalid.length > 0) {
    throw new SegmentError(
      `not GSM-7: ${check.invalid.map((c) => JSON.stringify(c)).join(' ')}`,
      check,
    )
  }
  if (check.septets > SEGMENT_SEPTETS) {
    throw new SegmentError(`${check.septets} septets, over one segment (${SEGMENT_SEPTETS})`, check)
  }
}

/** Common typographic characters phones and keyboards insert, mapped to GSM-7 look-alikes. */
const REPLACEMENTS: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '′': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '″': '"',
  '–': '-',
  '—': '-',
  '−': '-',
  '…': '...',
  ' ': ' ',
  ' ': ' ',
  ' ': ' ',
  '\t': ' ',
}

/**
 * Normalises user input towards GSM-7: smart quotes, dashes, ellipses and odd spaces become
 * their plain equivalents, and runs of whitespace collapse. It does not remove anything else:
 * an emoji stays, so the validator can tell the person what to change.
 */
export function toGsmFriendly(text: string): string {
  let out = ''
  for (const char of text.normalize('NFC')) out += REPLACEMENTS[char] ?? char
  return out.replace(/[ \r\n]+/g, ' ').trim()
}

/** Whether every character of `text` is GSM-7 (length aside). */
export function isGsm7(text: string): boolean {
  return checkSegment(text).invalid.length === 0
}
